import type { ComponentProps } from 'react'
import { ClockIcon, LoaderIcon, XCircleIcon } from 'lucide-react'
import type { components } from '@joyful/openapi/types.gen'
import type { EnumValueMeta } from '@joyful/utils'
import { cn, createEnumSchema } from '@joyful/utils'
import type { AgentConversationTabStatus } from '../../../core/components/agent/AgentConversationTabStatusIcon'

type AgentRunStatus = components['schemas']['AgentRunStatus']

interface AgentStatusMeta extends EnumValueMeta {
  icon: AgentConversationTabStatus['icon']
  isAnimated?: boolean
  variant: 'default' | 'muted'
}

export const AgentStatusSchema = createEnumSchema<
  AgentRunStatus,
  AgentStatusMeta
>([
  {
    description: 'The agent is working on this conversation.',
    icon: LoaderIcon,
    isAnimated: true,
    title: 'Agent working…',
    value: 'running',
    variant: 'default',
  },
  {
    description:
      'The agent is waiting on a longer-running step, usually a credential.',
    icon: ClockIcon,
    title: 'Waiting',
    value: 'hibernating',
    variant: 'muted',
  },
  {
    description: 'The agent needs a reply before it can continue.',
    icon: AwaitingHumanStatusIcon,
    title: 'Waiting for your input',
    value: 'awaiting_human',
    variant: 'muted',
  },
  {
    description: 'The agent hit an error in this conversation.',
    icon: XCircleIcon,
    title: 'Agent error',
    value: 'failed',
    variant: 'muted',
  },
])

export function toAgentConversationTabStatus(
  status: AgentRunStatus | null | undefined,
): AgentConversationTabStatus | null {
  if (!status) {
    return null
  }

  const statusMeta = AgentStatusSchema.meta(status)
  return {
    description: statusMeta.description,
    icon: statusMeta.icon,
    isAnimated: statusMeta.isAnimated,
    title: statusMeta.title,
    variant: statusMeta.variant,
  }
}

function AwaitingHumanStatusIcon({
  className,
  ...restProps
}: ComponentProps<'svg'>) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      {...restProps}
      className={cn(className, 'text-tremor-content-subtle')}
    >
      <circle cx="50%" cy="50%" r="3.5" strokeWidth="1" />
    </svg>
  )
}
